import { useState, useMemo, useEffect } from "react";
import Table from "examples/Tables/Table";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiProgress from "components/VuiProgress";
import { FaEye, FaPen, FaTrash, FaUser, FaUserShield } from "react-icons/fa";
import ViewRoleDialog from "./ViewRoleDialog";
import EditRoleDialog from "./EditRoleDialog";
import useCommon from "hooks/useCommon";
import { updateRole } from "store/slice/rolesSlice";
import Snackbar from "components/Snackbar";
import DeleteRoleDialog from "./DeleteRoleDialog";
import { deleteRole } from "store/slice/rolesSlice";
import { getRoles } from "store/slice/rolesSlice";
import NoResults from "components/NoResult";
import { clearDeleteRoleState } from "store/slice/rolesSlice";
import VuiTooltip from "components/VuiTooltip";
import { clearUpdateRoleState } from "store/slice/rolesSlice";

/* ---------------- Cells ---------------- */

function RoleNameCell({ title }) {
  return (
    <VuiBox px={1}>
      <VuiTypography variant="body2" fontWeight="regular">
        {title}
      </VuiTypography>
    </VuiBox>
  );
}

function RoleLevelCell({ power }) {
  const getColor = () => {
    if (power >= 100) return "success";
    if (power >= 80) return "warning";
    if (power >= 50) return "success";
    if (power >= 25) return "info";
    return "error";
  };

  return (
    <VuiBox width="150px" px={1}>
      <VuiProgress value={power} color={getColor()} />
    </VuiBox>
  );
}

function UsersAssignedCell({ count }) {
  return (
    <VuiBox display="flex" alignItems="center" gap={1} justifyContent="center">
      <FaUser />

      <VuiTypography variant="caption" fontWeight="medium">
        {count || 0}
      </VuiTypography>
    </VuiBox>
  );
}

function ActionButtons({ onEdit, onDelete, onView }) {
  return (
    <VuiBox display="flex" justifyContent="center" gap={1}>
      <VuiTooltip title="View role">
        <VuiButton size="small" color="primary" variant="outlined" onClick={onView} sx={{ px: 1 }}>
          <FaEye size={12} />
        </VuiButton>
      </VuiTooltip>

      <VuiTooltip title="Edit role">
        <VuiButton size="small" color="primary" variant="outlined" onClick={onEdit} sx={{ px: 1 }}>
          <FaPen size={12} />
        </VuiButton>
      </VuiTooltip>

      <VuiTooltip title="Delete role">
        <VuiButton size="small" color="error" variant="outlined" onClick={onDelete} sx={{ px: 1 }}>
          <FaTrash size={12} />
        </VuiButton>
      </VuiTooltip>
    </VuiBox>
  );
}

/* ---------------- Main Component ---------------- */

const RolesTable = ({ handleNotify, search }) => {
  const { DISPATCH, NAVIFATE, STORE } = useCommon();
  const hotelId = STORE((state) => state.ui.hotelId);
  const { roles, listLoading, pagination, deleteSuccess, createSuccess, updateSuccess } = STORE(
    (state) => state.roles
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [viewOpen, setViewOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const filteredRoles = useMemo(() => {
    if (!search || search.trim().length < 3) return roles;

    const q = search.toLowerCase();

    return roles.filter((role) => {
      return (
        role?.title?.toLowerCase().includes(q) ||
        String(role?.power)?.includes(q) ||
        String(role?.userCount || role?.users?.length || 0).includes(q)
      );
    });
  }, [roles, search]);

  const handleEditRole = async (roleId, data) => {
    await DISPATCH(updateRole({ roleId, data }))
      .unwrap()
      .then((res) => {
        const success = res?.success;

        if (success) {
          setEditOpen(false);

          DISPATCH(clearUpdateRoleState());

          handleNotify({
            color: success ? "success" : "error",
            title: success ? "Role Updated" : "Error",
            content: res?.message || "Role updated successfully",
            icon: success ? "check" : "warning",
            show: true,
          });
        }
      })
      .catch((err) => {
        handleNotify({
          color: "error",
          title: "Error",
          content: err?.message || "Failed to create client",
          icon: "warning",
          show: true,
        });
      });
  };

  const handleDeleteOpen = (role) => {
    setSelectedRole(role);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setSelectedRole(null);
    setDeleteOpen(false);
    DISPATCH(clearDeleteRoleState());
  };

  const handleDeleteConfirmation = async (roleId) => {
    if (!roleId || !hotelId) return;

    await DISPATCH(deleteRole({ roleId, hotelId }))
      .unwrap()
      .then((res) => {
        const success = res?.success;

        if (success) {
          handleNotify({
            color: success ? "success" : "error",
            title: success ? "Role Deleted" : "Error",
            content: res?.message || "Role deleted successfully",
            icon: success ? "check" : "warning",
            show: true,
          });

          handleDeleteClose();
        }
      })
      .catch((err) => {
        handleDeleteClose();
        handleNotify({
          color: "error",
          title: "Error",
          content: err?.message || "Failed to delete role",
          icon: "warning",
          show: true,
        });
      });
  };

  useEffect(() => {
    DISPATCH(getRoles({ status: "active" }));
  }, [hotelId, deleteSuccess, createSuccess, updateSuccess, DISPATCH]);

  const tableData = useMemo(() => {
    return {
      columns: [
        { name: "role", align: "left" },
        { name: "level", align: "left" },
        { name: "users", align: "center" },
        { name: "action", align: "center" },
      ],

      rows: filteredRoles.map((role) => ({
        role: <RoleNameCell title={role.title} />,

        level: <RoleLevelCell power={role.level} />,

        users: <UsersAssignedCell count={role.userCount || role.users?.length || 0} />,

        action: (
          <ActionButtons
            onView={() => {
              setSelectedRole(role);
              setViewOpen(true);
            }}
            onEdit={() => {
              setSelectedRole(role);
              setEditOpen(true);
            }}
            onDelete={() => handleDeleteOpen(role)}
          />
        ),
      })),
    };
  }, [filteredRoles, roles]);

  const isSearching = search && search.trim().length >= 3;
  const isSearchEmpty = isSearching && filteredRoles.length === 0;
  const isInitialEmpty = !isSearching && filteredRoles.length === 0;

  return (
    <>
      {isSearchEmpty ? (
        <NoResults title="No roles available" search={search} />
      ) : (
        <Table
          columns={tableData.columns}
          rows={tableData.rows}
          loading={listLoading}
          pagination
          paginationMode="client"
          page={page}
          rowsPerPage={rowsPerPage}
          total={tableData.rows.length}
          onPageChange={(newPage) => setPage(newPage)}
          onRowsPerPageChange={(newLimit) => {
            setRowsPerPage(newLimit);
            setPage(0);
          }}
          emptyState={isInitialEmpty}
          emptyTitle="No roles created yet"
        />
      )}

      {/* dialogs stay unchanged */}
      <ViewRoleDialog
        open={viewOpen}
        role={selectedRole}
        onClose={() => {
          setViewOpen(false);
          setSelectedRole(null);
        }}
      />

      <EditRoleDialog
        open={editOpen}
        onSave={handleEditRole}
        role={selectedRole}
        onClose={() => {
          setEditOpen(false);
          setEditingId(null);
        }}
      />

      <DeleteRoleDialog
        open={deleteOpen}
        role={selectedRole}
        onClose={handleDeleteClose}
        onConfirm={(role) => {
          handleDeleteConfirmation(role._id);
        }}
      />
    </>
  );
};

export default RolesTable;
